const MAX_DIAGNOSTIC_LENGTH = 100;

function getBoundedValue(value: unknown): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
    return undefined;
  }

  const stringValue = String(value);
  return stringValue.length > MAX_DIAGNOSTIC_LENGTH ? `${stringValue.slice(0, MAX_DIAGNOSTIC_LENGTH)}…` : stringValue;
}

export interface ErrorDiagnostics {
  errorType: string;
  errorName?: string;
  errorCode?: string;
  errorStatus?: string;
}

export function getErrorDiagnostics(error: unknown): ErrorDiagnostics {
  const diagnostics: ErrorDiagnostics = {
    errorType: error instanceof Error ? 'Error' : Array.isArray(error) ? 'array' : typeof error
  };

  if (!error || typeof error !== 'object') {
    return diagnostics;
  }

  try {
    const errorRecord = error as Record<string, unknown>;
    const errorName = getBoundedValue(errorRecord.name);
    const errorCode = getBoundedValue(errorRecord.code);
    const errorStatus = getBoundedValue(errorRecord.statusCode ?? errorRecord.status);

    if (errorName) {
      diagnostics.errorName = errorName;
    }
    if (errorCode) {
      diagnostics.errorCode = errorCode;
    }
    if (errorStatus) {
      diagnostics.errorStatus = errorStatus;
    }
  } catch {
    return { errorType: diagnostics.errorType };
  }

  return diagnostics;
}

const NETWORK_ERROR_MESSAGE_PATTERN =
  /failed to fetch|load failed|networkerror|network request failed|network error|network connection was lost|internet connection appears to be offline|err_internet_disconnected|err_network_changed/i;

const MAX_CAUSE_DEPTH = 3;

function hasNetworkErrorShape(error: unknown, depth = 0): boolean {
  if (!error || typeof error !== 'object' || depth > MAX_CAUSE_DEPTH) {
    return false;
  }

  const { name, message, statusCode, status, cause } = error as Record<string, unknown>;

  if (statusCode !== undefined || status !== undefined) {
    return false;
  }

  if (name === 'NetworkError' || (typeof message === 'string' && NETWORK_ERROR_MESSAGE_PATTERN.test(message))) {
    return true;
  }

  return hasNetworkErrorShape(cause, depth + 1);
}

/**
 * True when a request failed before any HTTP response was received (offline, DNS, CORS, dropped connection).
 * Errors that carry an HTTP status are never network errors. Failures can surface from `fetch` (`TypeError`),
 * auth/token-refresh libraries or wrapped errors, so the browser's offline state, the message and `cause` are all checked.
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const { statusCode, status } = error as Record<string, unknown>;

  if (statusCode !== undefined || status !== undefined) {
    return false;
  }

  return (typeof navigator !== 'undefined' && navigator.onLine === false) || hasNetworkErrorShape(error);
}
